/**
 * The v1_10_0 Broadcasts vocabulary: enum VALUES and limits, nothing else.
 *
 * A broadcast is one title, one description, one image and one destination,
 * sent as a push, an email, or both, with a separate audience per channel.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS AND IS NOT
 * ---------------------------------------------------------------------------
 * The ids below ARE the wire values — `route_key: "workouts"` really does send
 * the string `"workouts"`. That is deliberate: inventing a UI-only alias for a
 * closed server enum buys nothing and costs a translation table that can drift.
 *
 * What this file must never contain is a **field name**. `route_key`,
 * `push_segment`, `notification_title` and friends live in `broadcastPayload.js`
 * (requests) and `broadcastDecode.js` (responses), and nowhere else.
 */

/* ── Channels ─────────────────────────────────────────────────────────── */

/**
 * Every channel value a broadcast RECORD can carry, for labelling rows the API
 * hands back.
 *
 * `both` is in this list and NOT in `COMPOSE_MODES` below. The composer no
 * longer builds one — see that comment — but records created before it stopped
 * are still in the list and still have to render as something better than the
 * raw string.
 */
const CHANNELS = [
  { id: "push", label: "Popup only" },
  { id: "email", label: "Email only" },
  { id: "both", label: "Popup + email" },
];

export const CHANNEL_BY_ID = Object.fromEntries(
  CHANNELS.map((channel) => [channel.id, channel])
);

/** Does this channel selection send a push? An email? */
export const channelUsesPush = (channels) =>
  channels === "push" || channels === "both";
export const channelUsesEmail = (channels) =>
  channels === "email" || channels === "both";

/* ── Compose modes ────────────────────────────────────────────────────── */

/**
 * WHAT THE COMPOSER CAN BUILD: one popup, or one email. Never both at once.
 *
 * The two surfaces have nothing in common but a send button. A popup is one
 * glanceable line on a lock screen that opens a screen in the app; an email is
 * a subject competing with fifty others, a body with room to explain, and a
 * link out to the web. Composing them together forced every field to serve two
 * masters — the title WAS the subject, the description WAS the preheader — and
 * the result read like a compromise on both surfaces.
 *
 * So the mode is picked first and it picks everything else: which fields exist,
 * which audience is targeted, which preview is shown, and which half of the
 * payload is built. An author who wants both sends two broadcasts, each written
 * for the surface it lands on.
 *
 * `id` is still the wire value for `channels` — "push" and "email" are what the
 * API understands. Only the label is the author's word for it.
 */
export const COMPOSE_MODES = [
  {
    id: "push",
    label: "Popup",
    tagline: "A notification on the lock screen that opens the app.",
  },
  {
    id: "email",
    label: "Email",
    tagline: "A subject, a body and a button, delivered to the inbox.",
  },
];

const COMPOSE_MODE_BY_ID = Object.fromEntries(
  COMPOSE_MODES.map((mode) => [mode.id, mode])
);

export const DEFAULT_COMPOSE_MODE = "push";

/** Is this a mode the composer can actually build? `both` is not. */
export const isComposeMode = (id) => Boolean(COMPOSE_MODE_BY_ID[id]);

/* ── Destination ──────────────────────────────────────────────────────── */

/**
 * The six in-app destinations. A CLOSED list — the spec is explicit that
 * "anything else is a tap that goes nowhere", so the picker cannot offer a
 * free-text option and there is no `custom` sentinel.
 *
 * `deepLink` is display-only: the composer shows `flex://workouts` under the
 * picker so the author can see what the tap resolves to. Only the id is sent.
 */
export const ROUTES = [
  { id: "home", label: "Home", deepLink: "flex://home" },
  { id: "subscriptions", label: "Subscriptions", deepLink: "flex://subscriptions" },
  { id: "workouts", label: "Workouts", deepLink: "flex://workouts" },
  { id: "food", label: "Food", deepLink: "flex://food" },
  { id: "steps", label: "Steps", deepLink: "flex://steps" },
  { id: "chat", label: "Chat", deepLink: "flex://chat" },
];

export const ROUTE_BY_ID = Object.fromEntries(
  ROUTES.map((route) => [route.id, route])
);

export const DEFAULT_ROUTE = "home";

/**
 * The handful of web destinations an email is usually pointed at.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT `ROUTES`
 * ---------------------------------------------------------------------------
 * `ROUTES` are `flex://` deep links, which a mail client cannot open. An email
 * link has to be an `https` URL, so the two catalogues cannot be the same list
 * even though they name some of the same places.
 *
 * Unlike `ROUTES` this is a CONVENIENCE, not a closed set: the author can type
 * any URL they like and these are one click each. Adding one here does not
 * change what is sendable, only what is quick.
 */
export const EMAIL_DESTINATIONS = [
  { label: "Workouts", url: "https://flex.fit/workouts" },
  { label: "This week's plan", url: "https://flex.fit/plan" },
  { label: "Book a class", url: "https://flex.fit/classes" },
];

/* ── Audiences ────────────────────────────────────────────────────────── */

/**
 * Segments both channels understand.
 *
 * `custom` is listed because the API accepts it, but it carries no filter in
 * this build — a broadcast addresses exactly one named segment. It is
 * deliberately last so the useful presets lead.
 */
const SHARED_SEGMENTS = [
  { id: "all", label: "All app users" },
  { id: "filtered", label: "Saved filter" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
  { id: "unsubscribed", label: "Unsubscribed" },
  { id: "renewal_14d", label: "Renewal <14d" },
  { id: "inactive_48h", label: "Inactive 48h+" },
  { id: "custom", label: "Custom filter" },
];

/**
 * Email-only segments.
 *
 * These three exist because email reaches contacts who never installed the
 * app. They must NEVER be offered for push: a push segment of people with no
 * app is an empty send, and the composer should not let an author build one.
 */
const EMAIL_ONLY_SEGMENTS = [
  { id: "all_contacts", label: "All contacts" },
  { id: "app_users", label: "App users" },
  { id: "not_on_app", label: "Not on the app" },
];

/**
 * The saved-filter segment (v1_11_0).
 *
 * Unlike every other segment, this one carries an id: the broadcast sends
 * `push_segment: "filtered"` plus `push_filter_id`. One filter serves both
 * channels — it answers WHO, and each channel then applies its own
 * reachability, which is why the same filter yields two different `reachable`
 * numbers. That is correct, not a bug.
 */
const FILTERED_SEGMENT = "filtered";

/** Does this segment id require an accompanying filter id? */
export const segmentNeedsFilter = (id) => id === FILTERED_SEGMENT;

export const PUSH_SEGMENTS = SHARED_SEGMENTS;
export const EMAIL_SEGMENTS = [...EMAIL_ONLY_SEGMENTS, ...SHARED_SEGMENTS];

export const PUSH_SEGMENT_BY_ID = Object.fromEntries(
  PUSH_SEGMENTS.map((segment) => [segment.id, segment])
);
export const EMAIL_SEGMENT_BY_ID = Object.fromEntries(
  EMAIL_SEGMENTS.map((segment) => [segment.id, segment])
);

export const DEFAULT_PUSH_SEGMENT = "active";
export const DEFAULT_EMAIL_SEGMENT = "all_contacts";

/** Is this segment id valid for the given channel? Used by validation. */
export const isPushSegment = (id) => Boolean(PUSH_SEGMENT_BY_ID[id]);
export const isEmailSegment = (id) => Boolean(EMAIL_SEGMENT_BY_ID[id]);

/* ── Content limits ───────────────────────────────────────────────────── */

/**
 * One pair of limits, applied to whichever surface is being written.
 *
 * They are the tighter, email-shaped numbers even for a popup: the wire carries
 * the headline in `notification_title` and the supporting line in
 * `notification_body` whichever mode built them, and an over-length subject
 * truncates in the inbox — the one surface the composer cannot show.
 */
export const TITLE_MAX = 60;
export const BODY_MAX = 300;

/**
 * Image constraints.
 *
 * Dimensions are advisory — an off-size image is warned about, never blocked.
 * SIZE IS ALSO NOT A REJECTION: an oversized file is downscaled client-side to
 * fit this ceiling and then uploaded (see `src/utils/imageCompress.js`). A
 * phone photo is routinely 4-8 MB, and handing that back as an error makes the
 * author leave the app to solve something the browser can solve in a moment.
 * Only the TYPE is a hard gate.
 */
export const IMAGE_MAX_BYTES = 3 * 1024 * 1024;
export const IMAGE_WIDTH = 1200;
export const IMAGE_HEIGHT = 628;
export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png"];
export const IMAGE_HINT = `${IMAGE_WIDTH}×${IMAGE_HEIGHT} · JPG or PNG · larger images are resized`;

/* ── Scheduling ───────────────────────────────────────────────────────── */

export const SEND_MODES = [
  { id: "now", label: "Now" },
  { id: "later", label: "Schedule" },
];

/* ── List screen ──────────────────────────────────────────────────────── */

/**
 * Channel tabs. `all` is the absence of a filter, not a value — the service
 * omits the param entirely rather than sending `channel=all`.
 */
export const CHANNEL_TABS = [
  { id: "all", label: "All" },
  { id: "push", label: "Push" },
  { id: "email", label: "Email" },
];

/** Status filter chips. `all` again means "send no status param". */
export const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "scheduled", label: "Scheduled" },
  { id: "sent", label: "Sent" },
  { id: "draft", label: "Draft" },
];

/** Date-range presets, in days. */
export const DAY_PRESETS = [
  { id: 7, label: "Last 7 days" },
  { id: 30, label: "Last 30 days" },
  { id: 90, label: "Last 90 days" },
];

export const DEFAULT_DAYS = 30;

/* ── Detail screen ────────────────────────────────────────────────────── */

/** Recipients tab pills. */
export const RECIPIENT_STATES = [
  { id: "delivered", label: "Delivered" },
  { id: "pending", label: "Pending" },
  { id: "failed", label: "Failed" },
  { id: "suppressed", label: "Suppressed" },
];

/** Engagement tab pills. */
export const ENGAGEMENT_FILTERS = [
  { id: "clicked", label: "Clicked" },
  { id: "opened_no_click", label: "Opened, no click" },
  { id: "no_open", label: "No open" },
];

/**
 * Funnel stages per channel, in order. Email carries two extra terminal
 * stages that push has no equivalent for.
 */
export const PUSH_FUNNEL_STAGES = [
  { id: "queued", label: "Queued" },
  { id: "sent", label: "Sent" },
  { id: "delivered", label: "Delivered" },
  { id: "opened", label: "Opened" },
  { id: "clicked", label: "Clicked" },
];

export const EMAIL_FUNNEL_STAGES = [
  ...PUSH_FUNNEL_STAGES,
  { id: "suppressed", label: "Suppressed" },
  { id: "bounced", label: "Bounced" },
];

/* ── Timings ──────────────────────────────────────────────────────────── */

/** Debounce for the live audience estimate, in ms. */
export const ESTIMATE_DEBOUNCE_MS = 300;

/**
 * Poll interval for a broadcast that is still sending. A 100k send takes
 * hours by design, so this is a progress ticker, not a race.
 */
export const SENDING_POLL_MS = 15000;

/* ── Audience filters (v1_11_0) ───────────────────────────────────────── */

/**
 * Operators, by the field type they apply to.
 *
 * Time rules are RELATIVE on purpose. A saved filter re-resolves every time it
 * is used, so "onboarded in the last 30 days" still means the last 30 days
 * when the filter is reused next month — an absolute range saved in January
 * would still mean January in March.
 */
const OPERATORS_BY_TYPE = {
  enum: [
    { id: "in", label: "is any of", value: "list" },
    { id: "not_in", label: "is none of", value: "list" },
  ],
  text: [
    { id: "in", label: "is any of", value: "list" },
    { id: "not_in", label: "is none of", value: "list" },
  ],
  uuid: [
    { id: "in", label: "is any of", value: "list" },
    { id: "not_in", label: "is none of", value: "list" },
  ],
  number: [
    { id: "gte", label: "is at least", value: "number" },
    { id: "lte", label: "is at most", value: "number" },
    { id: "between", label: "is between", value: "range" },
  ],
  relative_time: [
    { id: "within_last", label: "in the last", value: "days" },
    { id: "more_than_ago", label: "more than", value: "days" },
    { id: "within_next", label: "in the next", value: "days" },
  ],
  boolean: [{ id: "is", label: "is", value: "boolean" }],
};

/** Operators valid for a field type, or [] for a type we do not render. */
export const operatorsFor = (fieldType) =>
  OPERATORS_BY_TYPE[String(fieldType || "").toLowerCase()] || [];

/** How a given operator's `value` should be edited. */
export const valueKindFor = (fieldType, operator) =>
  operatorsFor(fieldType).find((op) => op.id === operator)?.value || null;

/**
 * A filter with no rules is a 422, NOT "everyone".
 *
 * The `all` segment is how you address everyone. An empty rule list almost
 * always means the author is mid-thought, and sending it as a match-all would
 * be the most expensive possible misunderstanding.
 */
export const MIN_FILTER_RULES = 1;
